import React, { SyntheticEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Tabs from '@mui/material/Tabs/Tabs';
import Tab from '@mui/material/Tab/Tab';
import Box from '@mui/material/Box/Box';
import Paper from '@mui/material/Paper/Paper';
import AdminUsersTab from './AdminUsersTab';
import AdminWikiEntriesTab from './AdminWikiEntriesTab';
import AdminCompaniesTab from './AdminCompaniesTab';
import AdminCompanyPage from './AdminCompanyPage';
import { useStore } from '../../store/store';

type AdminTabKey = 'users' | 'wiki' | 'companies';

const TABS: { label: string; value: AdminTabKey }[] = [
  { label: 'Users', value: 'users' },
  { label: 'Wiki entries', value: 'wiki' },
  { label: 'Companies', value: 'companies' },
];

/** Where the Companies tab lives, and one company's page under it: a link a superAdmin can paste in a support chat. */
const COMPANIES_PATH = '/admin/companies';

const AdminPage: React.FC = observer(() => {
  const { adminCompaniesStore } = useStore();
  const history = useHistory();
  const companiesMatch = useRouteMatch(COMPANIES_PATH);
  const companyMatch = useRouteMatch<{ id: string }>(`${COMPANIES_PATH}/:id(\\d+)`);
  const isAtCompanies = companiesMatch !== null;
  const [activeTab, setActiveTab] = useState<AdminTabKey>(isAtCompanies ? 'companies' : 'users');

  // A pasted link, or back and forward, can bring the Companies tab's path while another tab shows.
  useEffect(() => {
    if (isAtCompanies) setActiveTab('companies');
  }, [isAtCompanies]);

  const handleTabChange = (_event: SyntheticEvent, newValue: AdminTabKey) => {
    setActiveTab(newValue);
    if (newValue === 'companies') {
      history.push(COMPANIES_PATH);
    } else if (isAtCompanies) {
      history.push('/admin');
    }
  };

  const renderCurrentTab = () => {
    switch (activeTab) {
      case 'wiki':
        return <AdminWikiEntriesTab />;
      case 'companies':
        if (companyMatch) {
          return <AdminCompanyPage adminCompaniesStore={adminCompaniesStore} companyId={Number(companyMatch.params.id)} onLeave={() => history.push(COMPANIES_PATH)} />;
        }
        return <AdminCompaniesTab adminCompaniesStore={adminCompaniesStore} onOpenCompany={(id) => history.push(`${COMPANIES_PATH}/${id}`)} />;
      case 'users':
      default:
        return <AdminUsersTab />;
    }
  };

  return (
    <Box sx={{ flex: '1 1 auto', width: '100%', px: 4, py: 3 }}>
      <Paper elevation={1} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 900, p: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
        <Box sx={{ flex: 1, py: 3, overflow: 'auto' }}>{renderCurrentTab()}</Box>
      </Paper>
    </Box>
  );
});

export default AdminPage;
